import { useEffect, useMemo, useState } from "react";
import {
  ClipboardList, Circle, Clock, CircleCheck, AlertTriangle,
  PlaneLanding, Sparkles, Hammer, Check, ConciergeBell,
} from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useProperty } from "../lib/PropertyContext";
import PropertySwitcher from "../components/customer/PropertySwitcher";
import Avatar from "../components/Avatar";
import StatusHistoryTimeline from "../components/StatusHistoryTimeline";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { effectiveBookingStatus, BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium, deDateShort } from "../lib/dateFormat";

/* ------------------------------------------------------------------
 *  Read-only requests overview
 *  Merges three sources into a chronological, unified list:
 *   1) /bookings          → arrival/departure plans
 *   2) /requests (catalog)→ ordered extras (with batch_id)
 *   3) /requests (custom) → free "Request Anything" tickets (no batch_id)
 * ------------------------------------------------------------------ */

function useStatusMeta(de) {
  return useMemo(() => ({
    new:                 { label: de ? "Neu" : "New",              bg: STATUS_COLORS.grey, fg: BRAND_COLORS.navy, icon: Circle },
    received:            { label: de ? "Eingegangen" : "Received", bg: STATUS_COLORS.grey, fg: BRAND_COLORS.navy, icon: Circle },
    requested:           { label: de ? "Angefragt" : "Requested",  bg: STATUS_COLORS.grey, fg: BRAND_COLORS.navy, icon: Circle },
    awaiting_approval:   { label: de ? "Freigabe offen" : "Awaiting approval", bg: STATUS_COLORS.grey, fg: BRAND_COLORS.navy, icon: Clock },
    under_review:        { label: de ? "In Prüfung" : "Under review",         bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, icon: Clock },
    quote_required:      { label: de ? "Angebot" : "Quote",                   bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, icon: Clock },
    scheduled:           { label: de ? "Geplant" : "Scheduled",               bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, icon: Clock },
    in_progress:         { label: de ? "In Betreuung" : "In service",          bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, icon: Clock },
    in_service:          { label: de ? "In Betreuung" : "In service",          bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, icon: Clock },
    confirmed:           { label: de ? "Bestätigt" : "Confirmed",             bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, icon: Clock },
    accepted:            { label: de ? "Bestätigt" : "Confirmed",             bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, icon: Clock },
    done:                { label: de ? "Erledigt" : "Done",                   bg: STATUS_COLORS.green, fg: BRAND_COLORS.ivory, icon: CircleCheck },
    completed:           { label: de ? "Erledigt" : "Done",                   bg: STATUS_COLORS.green, fg: BRAND_COLORS.ivory, icon: CircleCheck },
    closed:              { label: de ? "Geschlossen" : "Closed",              bg: STATUS_COLORS.green, fg: BRAND_COLORS.ivory, icon: CircleCheck },
    overdue:             { label: de ? "Überfällig" : "Overdue",              bg: STATUS_COLORS.red, fg: BRAND_COLORS.ivory, icon: AlertTriangle },
    urgent:              { label: de ? "Dringend" : "Urgent",                 bg: STATUS_COLORS.red, fg: BRAND_COLORS.ivory, icon: AlertTriangle },
    cancelled:           { label: de ? "Storniert" : "Cancelled",             bg: STATUS_COLORS.grey, fg: BRAND_COLORS.navy, icon: CircleCheck },
  }), [de]);
}

const DONE_STATUSES = new Set(["done", "completed", "closed", "cancelled"]);

function useTypeMeta(de) {
  return useMemo(() => ({
    arrival: { label: de ? "Ab-/Anreise"    : "Absence",       icon: PlaneLanding },
    extra:   { label: de ? "Zusatzleistung" : "Extra service", icon: Sparkles },
    custom:  { label: de ? "Anfrage"        : "Request",       icon: Hammer },
  }), [de]);
}

function TypeChip({ type, meta }) {
  const m = meta[type] || meta.custom;
  const Icon = m.icon;
  return (
    <span
      data-testid={`overview-type-${type}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] tracking-[0.14em] uppercase rounded-full border border-navy/20 text-navy-dark bg-white"
    >
      <Icon className="w-3 h-3" strokeWidth={1.8} />
      {m.label}
    </span>
  );
}

function StatusChip({ status, done, meta, de }) {
  const m = meta[status] || { label: status || "—", bg: "#e5e7eb", fg: "#374151", icon: Circle };
  // Feb 2026 · Sonderregel Storniert: Storniert ist zwar in `DONE_STATUSES`
  // enthalten (damit die Zeile in „Erledigt" landet), soll aber ROT gezeigt
  // werden — identisch zur systemweiten Storniert-Farbe im Kalender und in
  // der Kunden-Übersicht. Ohne diese Sonderregel würde der Chip fälschlich
  // grün leuchten (weil `done === true`).
  const isCancelled = status === "cancelled";
  const Icon = isCancelled ? AlertTriangle : (done ? CircleCheck : m.icon);
  const bg = isCancelled ? STATUS_COLORS.red : (done ? STATUS_COLORS.green : m.bg);
  const fg = isCancelled ? BRAND_COLORS.ivory : (done ? BRAND_COLORS.ivory : m.fg);
  return (
    <span
      data-testid={`overview-status-${status}`}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase rounded-full shrink-0"
      style={{ background: bg, color: fg }}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {isCancelled ? (de ? "Storniert" : "Cancelled") : (done ? (de ? "Erledigt" : "Done") : m.label)}
    </span>
  );
}

export default function CustomerRequests() {
  const { lang } = useI18n();
  const de = lang === "de";
  const STATUS_META = useStatusMeta(de);
  const TYPE_META = useTypeMeta(de);
  const formatDate = (iso) => { try { return deDateMedium(iso); } catch { return iso; } };
  const formatShortDate = (iso) => { try { return deDateShort(iso); } catch { return iso; } };

  const [bookings, setBookings] = useState(null);
  const [requests, setRequests] = useState(null);
  // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter global: `properties` +
  // `selected` aus dem Context konsumieren. `properties` wird lokal nur noch
  // fürs Namens-Lookup gebraucht.
  const { properties, selected } = useProperty();
  const [filter, setFilter] = useState("open");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [b, r] = await Promise.all([
          api.get("/bookings"),
          api.get("/requests"),
        ]);
        if (cancelled) return;
        setBookings(b.data || []);
        setRequests(r.data || []);
      } catch (e) {
        if (cancelled) return;
        console.error("CustomerRequests load failed:", e);
        setBookings([]);
        setRequests([]);
      }
    };
    load();
    api.post("/requests/mark-read").then(() => {
      window.dispatchEvent(new Event("weissblom:unread-refresh"));
    }).catch(() => {});
    const interval = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const propertyName = useMemo(() => {
    const m = {};
    (properties || []).forEach((p) => { m[p.id] = p.name; });
    return (id) => (id ? m[id] || "" : "");
  }, [properties]);

  const items = useMemo(() => {
    if (bookings === null || requests === null) return null;
    // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter: Bookings + Requests auf das
    // aktive Objekt einschränken (nur wenn ein Objekt gewählt UND der Kunde
    // mehr als eins hat — sonst wäre der Filter redundant).
    const propId = selected?.id;
    const filterProp = propId && (properties?.length || 0) > 1;
    const scopedBookings = filterProp
      ? bookings.filter((b) => b.property_id === propId)
      : bookings;
    const scopedRequests = filterProp
      ? requests.filter((r) => !r.property_id || r.property_id === propId)
      : requests;
    const out = [];
    const childrenByBooking = {};
    for (const r of scopedRequests) {
      if (r.booking_id) {
        (childrenByBooking[r.booking_id] = childrenByBooking[r.booking_id] || []).push(r);
      }
    }
    for (const b of scopedBookings) {
      const kids = childrenByBooking[b.id] || [];
      const effectiveStatus = effectiveBookingStatus(b);
      const done = effectiveStatus === "completed";
      const isInService = effectiveStatus === "in_service";
      out.push({
        id: `b-${b.id}`,
        type: "arrival",
        title: `${de ? "Abwesenheit" : "Absence"} · ${formatShortDate(b.from_date)} → ${formatShortDate(b.to_date)}`,
        description: b.notes || null,
        property: propertyName(b.property_id),
        at: b.created_at || b.from_date,
        status: effectiveStatus,
        done,
        active: isInService,
        priority: null,
        assignee: null,
        children: kids
          .slice()
          .sort((a, x) => (a.created_at || "").localeCompare(x.created_at || ""))
          .map((r) => ({
            id: r.id,
            label: r.title,
            description: r.description,
            done: DONE_STATUSES.has(r.status),
            status: r.status,
            priority: r.priority,
            created_by_team: !!r.created_by_team,
          })),
      });
    }
    for (const r of scopedRequests) {
      if (r.booking_id) continue;
      const done = DONE_STATUSES.has(r.status);
      const isExtra = !!r.batch_id;
      out.push({
        id: `r-${r.id}`,
        type: isExtra ? "extra" : "custom",
        title: r.title,
        description: r.description,
        property: propertyName(r.property_id),
        at: r.created_at,
        status: r.status,
        done,
        priority: r.priority,
        assignee: r.assignee || null,
        created_by_team: !!r.created_by_team,
        status_history: r.status_history || [],
      });
    }
    // Feb 2026 · Sortier-Directive (Kunden-App „Ihre Anfragen"):
    //   1. Stornierte Anfragen IMMER ans Ende, unabhängig vom Datum.
    //   2. Innerhalb der nicht-stornierten Einträge: chronologisch mit den
    //      nächsten zuerst (`at` descending — Buchungen fallen auf
    //      `from_date` zurück, freie Anfragen auf `created_at`).
    // Der Cancelled-Rutsch greift auch innerhalb des „Erledigt"-Akkordeons,
    // damit dort abgeschlossene Anfragen VOR den stornierten stehen.
    out.sort((a, b) => {
      const aCanc = a.status === "cancelled" ? 1 : 0;
      const bCanc = b.status === "cancelled" ? 1 : 0;
      if (aCanc !== bCanc) return aCanc - bCanc;
      return (b.at || "").localeCompare(a.at || "");
    });
    return out;
  }, [bookings, requests, propertyName, de, selected?.id, properties?.length]);

  const counts = useMemo(() => {
    const all = items || [];
    const c = { total: all.length, open: 0, done: 0 };
    for (const it of all) { if (it.done) c.done += 1; else c.open += 1; }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (filter === "open") return items.filter((i) => !i.done);
    if (filter === "done") return items.filter((i) => i.done);
    return items;
  }, [items, filter]);

  return (
    <div className="max-w-[900px] mx-auto px-5 md:px-10 py-8 md:py-12 pb-32" data-testid="customer-requests-overview">
      <div className="mb-6">
        <div className="label-caps mb-2">{de ? "Concierge" : "Concierge"}</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight">
          {de ? "Ihre Anfragen auf einen Blick" : "Your requests at a glance"}
        </h1>
        <p className="text-[14px] text-navy-dark/70 mt-2 max-w-xl leading-relaxed">
          {de
            ? "Alle Ab-/Anreise-Planungen, bestellten Zusatzleistungen und individuellen Anfragen — chronologisch und in Echtzeit."
            : "All absences, ordered extras and individual requests — chronological, in real time."}
        </p>
      </div>

      {/* Feb 2026 · 8 Sep 2026 · Globaler Objekt-Umschalter — Bookings +
          Requests filtern automatisch nach `selected.id`. */}
      <PropertySwitcher testidPrefix="requests-property-switcher" />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="overview-filter-chips">
          {[
            { key: "open", label: `${de ? "Aktive" : "Active"} (${counts.open})` },
            { key: "done", label: `${de ? "Erledigt" : "Done"} (${counts.done})` },
            { key: "all",  label: `${de ? "Alle" : "All"} (${counts.total})` },
          ].map((c) => (
            <button
              key={c.key}
              data-testid={`overview-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`whitespace-nowrap px-4 py-2 text-[12px] tracking-wider border transition-colors ${filter === c.key ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy-dark hover:border-navy/50"}`}
              style={filter === c.key ? { background: BRAND_COLORS.navy } : {}}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {items === null ? (
        <div className="bg-white border border-navy/10 p-8 text-center label-caps" data-testid="overview-loading">
          {de ? "Wird geladen …" : "Loading …"}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-ivory/60 border border-dashed border-navy/20 p-10 text-center" data-testid="overview-empty">
          <ClipboardList className="w-8 h-8 mx-auto mb-3" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.3} />
          <div className="font-display text-xl text-navy mb-1">{de ? "Keine Einträge" : "No entries"}</div>
          <div className="text-[13px] text-navy-dark/60">
            {filter === "done"
              ? (de ? "Noch keine erledigten Anfragen." : "No completed requests yet.")
              : filter === "open"
                ? (de ? "Alles ruhig — keine offenen Anfragen." : "All quiet — no open requests.")
                : (de ? "Sobald Sie eine Anfrage stellen, erscheint sie hier." : "As soon as you make a request it will appear here.")}
          </div>
        </div>
      ) : (
        <RequestList items={filtered} filter={filter} de={de}
          statusMeta={STATUS_META} typeMeta={TYPE_META} formatDate={formatDate} />
      )}
    </div>
  );
}

function RequestList({ items, filter, de, statusMeta, typeMeta, formatDate }) {
  const openItems = items.filter((it) => !it.done);
  const doneItems = items.filter((it) => it.done);
  if (filter === "open") {
    return <ul className="space-y-3" data-testid="overview-list">{openItems.map((it) => <RequestRow key={it.id} it={it} de={de} statusMeta={statusMeta} typeMeta={typeMeta} formatDate={formatDate} />)}</ul>;
  }
  if (filter === "done") {
    return <ul className="space-y-3" data-testid="overview-list">{doneItems.map((it) => <RequestRow key={it.id} it={it} de={de} statusMeta={statusMeta} typeMeta={typeMeta} formatDate={formatDate} />)}</ul>;
  }
  return (
    <div className="space-y-6" data-testid="overview-list">
      {openItems.length > 0 && (
        <section data-testid="overview-active-section">
          <div className="label-caps mb-3 flex items-center gap-2" style={{ color: BRAND_COLORS.gold }}>
            <span>{de ? "Aktive Anfragen" : "Active requests"}</span>
            <span className="text-navy-dark/40">·</span>
            <span className="text-navy-dark/60 tabular-nums">{openItems.length}</span>
          </div>
          <ul className="space-y-3">{openItems.map((it) => <RequestRow key={it.id} it={it} de={de} statusMeta={statusMeta} typeMeta={typeMeta} formatDate={formatDate} />)}</ul>
        </section>
      )}
      {doneItems.length > 0 && (
        <section data-testid="overview-done-section">
          <CollapsibleSection
            testid="overview-done-accordion"
            allDone={true}
            title={
              <div className="label-caps flex items-center gap-2" style={{ color: BRAND_COLORS.navyDark }}>
                <span>{de ? "Erledigt" : "Done"}</span>
                <span className="text-navy-dark/40">·</span>
                <span className="text-navy-dark/60 tabular-nums">{doneItems.length}</span>
              </div>
            }
            subtitle={de
              ? "Automatisch eingeklappt — Anklicken zum Ansehen der abgeschlossenen Anfragen."
              : "Collapsed by default — click to view completed requests."}
          >
            <ul className="space-y-3">{doneItems.map((it) => <RequestRow key={it.id} it={it} de={de} statusMeta={statusMeta} typeMeta={typeMeta} formatDate={formatDate} />)}</ul>
          </CollapsibleSection>
        </section>
      )}
    </div>
  );
}

function RequestRow({ it, de, statusMeta, typeMeta, formatDate }) {
  // Feb 2026 · Sonderdarstellung für stornierte Anfragen — identisch zur
  // Kunden-Übersicht: diagonale rote Streifen als Hintergrund, roter
  // 4px-Rand-Anker links. Das überschreibt sowohl die grüne „done"-Tönung
  // als auch die blaue „active"-Tönung, weil Storniert die stärkste
  // visuelle Signalfarbe verdient. Der Read-Only-Charakter der Karte ist
  // ohnehin gegeben — es gibt keine Edit-Buttons in dieser Übersicht.
  const isCancelled = it.status === "cancelled";
  const cancelledStyle = isCancelled ? {
    background: `repeating-linear-gradient(135deg, ${withAlpha(STATUS_COLORS.red, 0.10)} 0 6px, ${withAlpha(STATUS_COLORS.red, 0.04)} 6px 12px)`,
    borderLeftWidth: "4px",
    borderLeftColor: STATUS_COLORS.red,
    borderRightColor: withAlpha(BRAND_COLORS.navy, 0.06),
    borderTopColor: withAlpha(BRAND_COLORS.navy, 0.06),
    borderBottomColor: withAlpha(BRAND_COLORS.navy, 0.06),
  } : {};
  return (
    <li
      className={`bg-white border p-5 flex items-start justify-between gap-4 transition-opacity ${it.done && !isCancelled ? "opacity-75" : ""} ${it.active && !isCancelled ? "border-l-4" : (isCancelled ? "" : "border-navy/10")}`}
      style={{
        ...(it.done && !isCancelled ? { background: withAlpha(STATUS_COLORS.green, 0.04) } : {}),
        ...(it.active && !isCancelled ? { background: withAlpha(STATUS_COLORS.blue, 0.045), borderLeftColor: STATUS_COLORS.blue, borderRightColor: withAlpha(BRAND_COLORS.navy, 0.06), borderTopColor: withAlpha(BRAND_COLORS.navy, 0.06), borderBottomColor: withAlpha(BRAND_COLORS.navy, 0.06) } : {}),
        ...cancelledStyle,
      }}
      data-testid={`overview-item-${it.id}${isCancelled ? "-cancelled" : (it.done ? "-done" : it.active ? "-in-service" : "")}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <TypeChip type={it.type} meta={typeMeta} />
          {it.created_by_team && (
            <span
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-full border"
              style={{ color: BRAND_COLORS.goldDark, borderColor: withAlpha(BRAND_COLORS.gold, 0.5), background: withAlpha(BRAND_COLORS.gold, 0.12) }}
              data-testid={`overview-created-by-team-${it.id}`}
              title={de ? "Vom Weissblom-Team in Ihrem Auftrag angelegt (Telefon/E-Mail)" : "Created by the Weissblom team on your behalf (phone/email)"}
            >
              <ConciergeBell className="w-2.5 h-2.5" strokeWidth={1.8} />
              {de ? "von Weissblom hinzugefügt" : "added by Weissblom"}
            </span>
          )}
          <span className="label-caps text-navy-dark/60">{formatDate(it.at)}</span>
          {it.property && (<span className="text-[11px] text-navy-dark/60">· {it.property}</span>)}
          {it.priority && it.priority !== "normal" && (
            <span
              className="label-caps"
              style={{ color: it.priority === "urgent" ? STATUS_COLORS.red : BRAND_COLORS.gold }}
            >
              · {it.priority === "urgent" ? (de ? "Dringend" : "Urgent") : (de ? "Hoch" : "High")}
            </span>
          )}
        </div>

        <div className="flex items-start gap-2">
          {it.done && (
            <span
              className="mt-1 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
              style={{ background: STATUS_COLORS.green }}
              aria-hidden
              data-testid={`overview-check-${it.id}`}
            >
              <Check className="w-3 h-3" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />
            </span>
          )}
          <div className="min-w-0">
            <h3 className={`font-display text-lg md:text-xl text-navy mb-1.5 leading-tight ${it.done ? "line-through decoration-navy-dark/25 text-navy-dark/60" : ""}`}>
              {it.title}
            </h3>
            {it.description && (
              <p className={`text-[13px] leading-relaxed ${it.done ? "text-navy-dark/50" : "text-navy-dark/70"}`}>
                {it.description}
              </p>
            )}
          </div>
        </div>

        {it.assignee && (
          <div className="mt-3 flex items-center gap-2 text-[12px] border-t border-navy/5 pt-3" data-testid={`overview-assignee-${it.id}`}>
            <Avatar name={it.assignee.name} fileId={it.assignee.avatar_file_id} size={28} />
            <span className="text-navy-dark/70">{de ? "Bearbeitet von" : "Handled by"}</span>
            <span className="text-navy font-medium">{it.assignee.name}</span>
          </div>
        )}

        {it.type === "arrival" && it.children && it.children.length > 0 && (
          <div
            className="mt-4 pl-4 border-l-2"
            style={{ borderColor: it.done ? withAlpha(STATUS_COLORS.green, 0.35) : withAlpha(BRAND_COLORS.gold, 0.4) }}
            data-testid={`overview-booking-children-${it.id}`}
          >
            <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>
              {de ? "Zusatzleistungen" : "Extras"} · {it.children.filter((c) => c.done).length}/{it.children.length} {de ? "erledigt" : "done"}
            </div>
            <ul className="space-y-1.5">
              {it.children.map((c) => (
                <li
                  key={c.id}
                  className="flex items-start gap-2 text-[13px] leading-snug"
                  data-testid={`overview-booking-child-${c.id}${c.done ? "-done" : ""}`}
                >
                  <span
                    className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={c.done
                      ? { background: STATUS_COLORS.green }
                      : { border: `1.5px solid ${withAlpha(BRAND_COLORS.navy, 0.35)}`, background: "#fff" }}
                    aria-hidden
                  >
                    {c.done && <Check className="w-2.5 h-2.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />}
                  </span>
                  <span className={c.done ? "line-through text-navy-dark/50" : "text-navy-dark/85"}>
                    {c.label}
                  </span>
                  {c.priority && c.priority !== "normal" && (
                    <span className="label-caps ml-1"
                      style={{ color: c.priority === "urgent" ? STATUS_COLORS.red : BRAND_COLORS.gold }}>
                      {c.priority === "urgent" ? (de ? "Dringend" : "Urgent") : (de ? "Hoch" : "High")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {it.type !== "arrival" && it.status_history && it.status_history.length > 0 && (
          <StatusHistoryTimeline
            history={it.status_history}
            currentStatus={it.status}
            testid={`overview-status-history-${it.id}`}
          />
        )}
      </div>
      <StatusChip status={it.status} done={it.done} meta={statusMeta} de={de} />
    </li>
  );
}
